import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles(theme => ({
  root: {
    // !important resists parent rules like `& span { fontFamily: ... }` that
    // otherwise override the icon font and render ligature names as text.
    fontFamily: '"Material Symbols Rounded Variable" !important',
    fontWeight: 'normal',
    fontStyle: 'normal',
    lineHeight: 1,
    letterSpacing: 'normal',
    textTransform: 'none',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    wordWrap: 'normal',
    direction: 'ltr',
    fontFeatureSettings: '"liga"',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
    userSelect: 'none',
    flexShrink: 0,
    fontSize: 24,
    width: '1em',
    height: '1em',
    overflow: 'hidden',
    verticalAlign: 'middle',
  },
  fontSizeInherit: { fontSize: 'inherit' },
  fontSizeSmall: { fontSize: 20 },
  fontSizeLarge: { fontSize: 35 },
  colorPrimary: { color: theme.palette.primary.main },
  colorSecondary: { color: theme.palette.secondary.main },
  colorAction: {
    color: (theme.palette.action && theme.palette.action.active) || undefined,
  },
  colorError: { color: theme.palette.error && theme.palette.error.main },
  colorDisabled: {
    color: (theme.palette.action && theme.palette.action.disabled) || undefined,
  },
}))

const resolveOpticalSize = (fontSize, numericFontSize, opticalSize) => {
  if (opticalSize != null) return opticalSize
  if (numericFontSize != null) return numericFontSize
  if (fontSize === 'small') return 20
  if (fontSize === 'large') return 40
  return 24
}

const MaterialSymbol = React.forwardRef(function MaterialSymbol(
  {
    name,
    children,
    fontSize = 'medium',
    color = 'inherit',
    weight = 300,
    fill = 0,
    grade = 0,
    opticalSize,
    htmlColor,
    className,
    style,
    ...rest
  },
  ref,
) {
  const classes = useStyles()
  const symbolName = name || children

  const numericFontSize = typeof fontSize === 'number' ? fontSize : null
  const resolvedOpticalSize = resolveOpticalSize(
    fontSize,
    numericFontSize,
    opticalSize,
  )

  // The rendered element is a generic <span>. When a consumer provides an
  // accessible name, mark the span as an image so assistive tech reliably
  // exposes that name; when no name is provided, hide the ligature text.
  const hasAccessibleName = Boolean(
    rest['aria-label'] || rest['aria-labelledby'],
  )

  return (
    <span
      ref={ref}
      aria-hidden={hasAccessibleName ? undefined : 'true'}
      role={hasAccessibleName ? 'img' : undefined}
      className={clsx(
        classes.root,
        // Compatibility classes so existing selectors that targeted MUI's
        // SvgIcon (e.g. `& .MuiSvgIcon-root`, `.MuiSvgIcon-fontSizeSmall`,
        // `.MuiSvgIcon-colorPrimary`) keep matching after the codemod
        // migration from <SvgIcon> to <MaterialSymbol>.
        'MuiSvgIcon-root',
        'material-symbol',
        {
          [classes.fontSizeInherit]: fontSize === 'inherit',
          [classes.fontSizeSmall]: fontSize === 'small',
          [classes.fontSizeLarge]: fontSize === 'large',
          [classes.colorPrimary]: color === 'primary',
          [classes.colorSecondary]: color === 'secondary',
          [classes.colorAction]: color === 'action',
          [classes.colorError]: color === 'error',
          [classes.colorDisabled]: color === 'disabled',
          'MuiSvgIcon-fontSizeInherit': fontSize === 'inherit',
          'MuiSvgIcon-fontSizeSmall': fontSize === 'small',
          'MuiSvgIcon-fontSizeLarge': fontSize === 'large',
          'MuiSvgIcon-colorPrimary': color === 'primary',
          'MuiSvgIcon-colorSecondary': color === 'secondary',
          'MuiSvgIcon-colorAction': color === 'action',
          'MuiSvgIcon-colorError': color === 'error',
          'MuiSvgIcon-colorDisabled': color === 'disabled',
        },
        className,
      )}
      style={{
        fontVariationSettings: `'wght' ${weight}, 'FILL' ${fill}, 'GRAD' ${grade}, 'opsz' ${resolvedOpticalSize}`,
        ...(numericFontSize !== null ? { fontSize: numericFontSize } : null),
        ...(htmlColor ? { color: htmlColor } : null),
        ...style,
      }}
      {...rest}
    >
      {symbolName}
    </span>
  )
})

MaterialSymbol.propTypes = {
  name: PropTypes.string,
  children: PropTypes.node,
  fontSize: PropTypes.oneOfType([
    PropTypes.oneOf(['inherit', 'small', 'medium', 'default', 'large']),
    PropTypes.number,
  ]),
  color: PropTypes.oneOf([
    'inherit',
    'primary',
    'secondary',
    'action',
    'error',
    'disabled',
  ]),
  weight: PropTypes.number,
  fill: PropTypes.oneOf([0, 1]),
  grade: PropTypes.number,
  opticalSize: PropTypes.number,
  htmlColor: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
}

export default MaterialSymbol
